import NiceModal from '@ebay/nice-modal-react';
import { enqueueSnackbar } from 'notistack';
import { createAction } from 'redux-actions';

import { hasPermissionByIds } from '@gef-ui/cdu-user/selectors';
import { replaceData, resetForm } from '@gef-ui/form-core/stream/transfer/control';
import { getLabelMessage } from '@gef-ui/localization/messages';

import MODALS from 'constants/modals';
import { KMP_NEW_CAMPAIGN } from 'features/common/acl/constants';
import dialog from 'features/common/dialog';
import fetch from 'features/common/fetch';
import { resetErrorsByPage } from 'features/common/submitFormDialog/slice';
import templates from 'features/views/campaignSettings/templates/';
import user from 'features/views/user';

import * as types from './actionTypes';
import { MAX_CONTACT_DATA_COLUMNS } from './constants';
import { CAMPAIGN_SETTINGS_CAMPAIGN_FORM_NAME } from './form';
import { getColumns, getDetail } from './selectors';
import { bodyFactory, formDataFactory } from './utils';

export const setCampaignDetail = createAction(types.SET_CAMPAIGN_DETAIL, (detail) => ({ detail }));
export const addContactDataColumn = createAction(types.ADD_CONTACT_DATA_COLUMN, (column) => ({ column }));
export const removeContactDataColumn = createAction(types.REMOVE_CONTACT_DATA_COLUMN, (column) => ({ column }));

export const addContactDataColumnWithMaxItemsCheck = (column) => (dispatch, getState) => {
	const state = getState();
	if (getColumns(state).length >= MAX_CONTACT_DATA_COLUMNS) {
		NiceModal.show(MODALS.lightbox_modal, {
			title: getLabelMessage('lightBox:maximumContactColumnsExceeded', { count: MAX_CONTACT_DATA_COLUMNS }),
			closeButtonLabel: 'common:ok',
			showConfirmButton: false,
		});
	} else {
		return dispatch(addContactDataColumn(column));
	}
};

export const fetchCampaignDetail =
	(campaignId) =>
	(dispatch, getState, { sendControlNext }) => {
		const newOrEditCampaignPermission = hasPermissionByIds(getState())(KMP_NEW_CAMPAIGN);

		dispatch(
			fetch.actions.get(fetch.constants.urls.campaignDetailWithoutConsideringRole, {
				replaceParams: { id: campaignId },
				onSuccess: [
					(response) => () => {
						const campaignDetail = response.body;
						if (!campaignDetail) return;
						dispatch(setCampaignDetail(campaignDetail));
						sendControlNext(CAMPAIGN_SETTINGS_CAMPAIGN_FORM_NAME, resetForm());
						sendControlNext(
							CAMPAIGN_SETTINGS_CAMPAIGN_FORM_NAME,
							replaceData({ data: formDataFactory(campaignDetail, newOrEditCampaignPermission) }),
						);
						dispatch(templates.actions.resetTemplateTable());
					},
				],
			}),
		);
	};

export const deleteCampaignContacts = (campaignId) => (dispatch) => {
	dispatch(
		fetch.actions.deleteRequest(fetch.constants.urls.campaignDeleteContacts, {
			replaceParams: { id: campaignId },
			onSuccess: [
				(response) => () => {
					const { deletedContactsCount } = response.body;
					if (deletedContactsCount > 0) {
						enqueueSnackbar(getLabelMessage('toastr:campaignSettingsDeleteContactsSuccess'), {
							variant: 'success',
						});
					}
					dispatch(user.actions.fetchAdditionalUserInfo());
					dispatch(fetchCampaignDetail(campaignId));
				},
			],
		}),
	);
};

export const saveCampaignSettings =
	(campaignId) =>
	async (dispatch, getState, { getFormData }) => {
		const formData = await getFormData(CAMPAIGN_SETTINGS_CAMPAIGN_FORM_NAME);
		const campaignDetail = getDetail(getState());
		const newOrEditCampaignPermission = hasPermissionByIds(getState())(KMP_NEW_CAMPAIGN);

		dispatch(dialog.actions.close(CAMPAIGN_SETTINGS_CAMPAIGN_FORM_NAME));
		dispatch(resetErrorsByPage({ page: CAMPAIGN_SETTINGS_CAMPAIGN_FORM_NAME }));

		const saveNewSettings = () =>
			dispatch(
				fetch.actions.put(fetch.constants.urls.campaignDetail, {
					uniqueReqID: 'put',
					replaceParams: { id: campaignId },
					body: bodyFactory(formData, campaignDetail, newOrEditCampaignPermission),
					onSuccess: [
						() => () => {
							enqueueSnackbar(getLabelMessage('toastr:approveSaveCampaign'), { variant: 'success' });
							dispatch(fetchCampaignDetail(campaignId));
						},
					],
				}),
			);

		if (campaignDetail?.nominationDate && campaignDetail?.channelId !== Number(formData?.channel)) {
			NiceModal.show(MODALS.lightbox_modal, {
				title: getLabelMessage('lightBox:campaignSettingsChannelChange'),
				onConfirm: saveNewSettings,
			});
		} else {
			saveNewSettings();
		}
	};

export const resetCampaignSettings = (campaignId, onSuccess) => (dispatch) => {
	dispatch(
		fetch.actions.put(fetch.constants.urls.campaignReset, {
			replaceParams: { id: campaignId },
			onSuccess: [
				() => () => {
					dispatch(fetchCampaignDetail(campaignId));
					enqueueSnackbar(getLabelMessage('toastr:approveSettingsReset'), { variant: 'success' });
					onSuccess && onSuccess();
				},
			],
		}),
	);
};
